import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDashboard } from "../context/DashboardContext";
import api from "../lib/api";
import { FavoritePairCard, AddPairCard } from "../components/dashboard/FavoritePairCard";
import AddPairDialog from "../components/dashboard/AddPairDialog";
import ChartPanel from "../components/dashboard/ChartPanel";
import ConfluenceRing from "../components/dashboard/ConfluenceRing";
import ActiveTradingPlan from "../components/dashboard/ActiveTradingPlan";
import MarketOverview from "../components/dashboard/MarketOverview";

export default function CommandCenter() {
  const { pairs, activeSymbol, setActiveSymbol, interval, setIntervalTf, removePair, livePrices } = useDashboard();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [overview, setOverview] = useState({ rows: [] });
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const analysisReqId = useRef(0);

  const activePair = pairs.find((p) => p.symbol === activeSymbol);

  // Card data is derived from the single overview request (price + scores + regime)
  const cardData = {};
  (overview.rows || []).forEach((r) => {
    if (!r.error) cardData[r.symbol] = {
      price: r.price, change24h: r.change24h, provider: r.provider,
      longScore: r.longScore, shortScore: r.shortScore, regime: r.regime,
    };
  });
  // Overlay live streamed prices for instant ticking
  Object.entries(livePrices || {}).forEach(([sym, lp]) => {
    if (cardData[sym]) {
      cardData[sym] = { ...cardData[sym], price: lp.price, change24h: lp.change24h, provider: lp.provider };
    }
  });

  // Load full analysis for active symbol
  const loadAnalysis = useCallback(async (showLoader = true) => {
    if (!activeSymbol) return;
    const rid = ++analysisReqId.current;
    if (showLoader) setAnalysisLoading(true);
    try {
      const a = await api.getAnalysis(activeSymbol, interval);
      if (rid === analysisReqId.current) setAnalysis(a);
    } catch { /* noop */ } finally { if (rid === analysisReqId.current) setAnalysisLoading(false); }
  }, [activeSymbol, interval]);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try { const o = await api.getOverview(interval); setOverview(o); } catch { /* */ } finally { setOverviewLoading(false); }
  }, [interval]);

  useEffect(() => { loadAnalysis(true); }, [loadAnalysis]);
  useEffect(() => {
    // light price refresh for active analysis without loader
    const t = setInterval(() => loadAnalysis(false), 15000);
    return () => clearInterval(t);
  }, [loadAnalysis]);
  useEffect(() => { loadOverview(); const t = setInterval(loadOverview, 30000); return () => clearInterval(t); }, [loadOverview]);

  const handleCreatePlan = (symbol) => {
    // P2B-7: read-only navigation to Trade Planner (no silent write on a single click).
    setActiveSymbol(symbol);
    navigate("/planner");
  };

  return (
    <div className="ym-fade space-y-4">
      {/* Favorite pair cards */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {pairs.map((p) => (
          <FavoritePairCard
            key={p.id}
            pair={p}
            data={cardData[p.symbol]}
            active={p.symbol === activeSymbol}
            onSelect={setActiveSymbol}
            onOpenChart={(s) => { setActiveSymbol(s); navigate("/chart"); }}
            onCreatePlan={handleCreatePlan}
            onDetails={(s) => { setActiveSymbol(s); }}
            onRemove={async (id) => { await removePair(id); toast.success("Pair removed"); }}
          />
        ))}
        <AddPairCard onClick={() => setAddOpen(true)} />
      </div>

      {/* Main grid: chart (left) + right column */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <ChartPanel
            symbol={activeSymbol}
            base={activePair?.base || activeSymbol.replace("USDT", "")}
            interval={interval}
            setInterval={setIntervalTf}
            analysis={analysis}
            livePrice={livePrices?.[activeSymbol]?.price}
          />
        </div>
        <div className="space-y-4">
          <ConfluenceRing analysis={analysis} />
          <ActiveTradingPlan analysis={analysis} loading={analysisLoading} onRecalculate={() => { loadAnalysis(true); toast.success("Recalculating from live data…"); }} />
        </div>
      </div>

      {/* Bottom market table */}
      <MarketOverview
        rows={overview.rows}
        loading={overviewLoading}
        onSelect={setActiveSymbol}
        onCreatePlan={handleCreatePlan}
      />

      <AddPairDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
